import React, { useState, useEffect, useCallback, useMemo } from "react";
import axios from "axios";
import { BASE_URL } from "../../../config";
import { getAuthHeaders } from "../../../services/useAuth";
import CustomButton from "../../../pages/CustomButton";
import Loader from "../../../dist/Loader";
import { toast } from "react-toastify";
const toISODate = (d) => {
  const dd = new Date(d);
  if (isNaN(dd)) return "";
  const yyyy = dd.getFullYear();
  const mm = String(dd.getMonth() + 1).padStart(2, "0");
  const day = String(dd.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${day}`;
};

const eachDate = (start, end) => {
  const s = new Date(start);
  const e = new Date(end);
  const out = [];
  if (isNaN(s) || isNaN(e) || s > e) return out;
  for (let d = new Date(s); d <= e; d.setDate(d.getDate() + 1)) {
    out.push(new Date(d));
  }
  return out;
};

const formatReadableDate = (value) => {
  if (!value) return "";
  const d = new Date(value);
  if (isNaN(d)) return "";
  return d.toLocaleDateString("en-GB", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
};

/**
 * mapRow
 * - preserves existing keys used across the component
 * - normalizes different possible shapes of "substate" into two fields:
 *    - substate (keeps original naming)
 *    - sub_state (table expects this)
 */
const mapRow = (raw) => {
  // derive a safe substate string from many possible shapes
  const substateCandidate =
    // common shapes
    (raw.substate &&
      (typeof raw.substate === "object" ? raw.substate.name : raw.substate)) ??
    (raw.sub_state &&
      (typeof raw.sub_state === "object"
        ? raw.sub_state.name
        : raw.sub_state)) ??
    raw.substate_name ??
    raw.sub_state_name ??
    raw.zone ??
    raw.area ??
    "";

  const substateSafe =
    substateCandidate === null ||
    substateCandidate === undefined ||
    substateCandidate === ""
      ? "-"
      : String(substateCandidate);

  return {
    emp_code: raw.emp_code ?? "-",
    user_id: raw.user_id ?? "-",
    employee: raw.employee ?? "-",
    state: raw.state ?? "-",
    substate: substateSafe,
    sub_state: substateSafe,
    doj: raw.doj ?? "-",
    manager: raw.manager ?? "-",
    designation: raw.designation ?? "-",
    division: raw.division ?? "-",
    headquarter: raw.headquarter ?? "-",
    working_date: raw.working_date ?? "-",
    retailer_name: raw.retailer_name ?? "-",
    retailer_id: raw.retailer_id ?? "-",
    visit_id: raw.visit_id ?? "-",
    product_id: raw.product_id ?? raw.productCode ?? raw.product_id ?? "-",
    product_code: raw.product_code ?? raw.productCode ?? "-",
    product_name: raw.product_name ?? raw.product ?? "-",
    quantity: raw.quantity ?? null,
    rate: raw.rate ?? 0,
    amount: raw.amount ?? 0,
  };
};

const StateWiseProduct = () => {
  const [states, setStates] = useState([{ id: "All", name: "All" }]);
  const [divisions, setDivisions] = useState([{ id: "All", name: "All" }]);
  const [substates, setSubstates] = useState([{ id: "All", name: "All" }]);

  const [selectedState, setSelectedState] = useState("");
  const [selectedSubState, setSelectedSubState] = useState("All");
  const [selectedDivision, setSelectedDivision] = useState("All");
  const [employees, setEmployees] = useState([
    { id: "All", first_name: "All", last_name: "" },
  ]);
  const [selectedEmployee, setSelectedEmployee] = useState("All");

  const today = new Date();
  const firstOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);
  const [startDate, setStartDate] = useState(toISODate(firstOfMonth));
  const [endDate, setEndDate] = useState(toISODate(today));

  const [tableData, setTableData] = useState([]);
  const [showContainers, setShowContainers] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  // Edit states - using combination of visit_id and product_id for uniqueness
  const [editingRowKey, setEditingRowKey] = useState(null);
  const [editedQuantities, setEditedQuantities] = useState({});

  const dateList = useMemo(
    () => eachDate(startDate, endDate),
    [startDate, endDate],
  );

  useEffect(() => {
    const fetchStates = async () => {
      try {
        const res = await axios.get(`${BASE_URL}/api/states`, {
          headers: getAuthHeaders(),
        });
        const raw = res.data || [];
        const list = Array.isArray(raw) ? raw : raw.data || [];
        setStates([{ id: "All", name: "All" }, ...list]);
      } catch (err) {
        console.error(err);
        setError("Failed to load states");
      }
    };
    fetchStates();
  }, []);

  useEffect(() => {
    if (!selectedState) {
      setDivisions([{ id: "All", name: "All" }]);
      setSelectedDivision("All");
      return;
    }
    const fetchDivisions = async () => {
      try {
        const res = await axios.get(
          `${BASE_URL}/api/divisions?state_id=${selectedState}`,
          { headers: getAuthHeaders() },
        );
        const list = res.data || [];
        setDivisions([{ id: "All", name: "All" }, ...list]);
        const found = list.find(
          (d) => String(d.id) === String(selectedDivision),
        );
        if (!found) setSelectedDivision("All");
      } catch (err) {
        console.error("divisions load failed", err);
        setError("Failed to load divisions");
      }
    };
    fetchDivisions();
  }, [selectedState]);

  useEffect(() => {
    if (!selectedState) {
      setSubstates([{ id: "All", name: "All" }]);
      return;
    }
    const fetchSubstates = async () => {
      try {
        const res = await axios.get(
          `${BASE_URL}/api/substates?state_id=${selectedState}`,
          { headers: getAuthHeaders() },
        );
        const list = res.data || [];
        setSubstates([{ id: "All", name: "All" }, ...list]);
        setSelectedSubState("All");
      } catch (err) {
        console.error("substates load failed", err);
        setError("Failed to load substates");
      }
    };
    fetchSubstates();
  }, [selectedState]);

  const fetchEmployees = useCallback(async () => {
    try {
      const params = new URLSearchParams();

      if (selectedState) {
        params.set("state_id", selectedState);
      }

      if (selectedSubState && selectedSubState !== "All") {
        params.set("substate_id", selectedSubState);
      }

      // ✅ ADD THIS BLOCK (CRITICAL FIX)
      if (selectedDivision && selectedDivision !== "All") {
        const divObj = divisions.find(
          (d) => String(d.id) === String(selectedDivision),
        );
        if (divObj?.name) {
          params.set("division", divObj.name);
        }
      }

      console.log("EMPLOYEE API:", params.toString()); // 🔍 debug

      const res = await axios.get(
        `${BASE_URL}/api/employees?${params.toString()}`,
        { headers: getAuthHeaders() },
      );

      const list = Array.isArray(res.data) ? res.data : res.data?.data || [];

      const normalized = list.map((e, i) => ({
        id: e.id ?? e.employee_id ?? e.emp_id ?? String(i + 1),
        first_name: e.first_name ?? "",
        last_name: e.last_name ?? "",
        employee: e.employee ?? e.emp_code ?? "",
        designation: e.designation ?? "",
        headquarter_name: e.headquarter_name ?? "",
        division: e.division ?? "",
        state_name: e.state_name ?? "",
      }));

      setEmployees([
        { id: "All", first_name: "All", last_name: "" },
        ...normalized,
      ]);

      setSelectedEmployee("All");
    } catch (err) {
      console.error("employees load failed", err);
    }
  }, [selectedState, selectedSubState, selectedDivision, divisions]);

  useEffect(() => {
    fetchEmployees();
  }, [selectedState, selectedSubState, fetchEmployees]);

  const handleGoClick = async () => {
    if (!startDate || !endDate) {
      setError("Please select start and end date");
      return;
    }
    if (new Date(startDate) > new Date(endDate)) {
      setError("Start date must be before end date");
      return;
    }
    setIsLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams();
      if (selectedState && selectedState !== "All") {
        params.set("state_id", selectedState);
      }
      if (selectedSubState && selectedSubState !== "All") {
        params.set("substate_id", selectedSubState);
      }
      if (selectedDivision && selectedDivision !== "All") {
        const divObj = divisions.find(
          (d) => String(d.id) === String(selectedDivision),
        );
        if (divObj?.name) {
          params.set("division", divObj.name);
        }
      }
      if (selectedEmployee && selectedEmployee !== "All") {
        params.set("employee_id", selectedEmployee);
      }
      params.set("start_date", startDate);
      params.set("end_date", endDate);
      const url = `${BASE_URL}/api/reports/ff-product-wise?${params.toString()}`;
      const res = await axios.get(url, { headers: getAuthHeaders() });

      // Response payload
      const payload = res.data?.products || res.data?.data || res.data || [];
      // Normalize rows via mapRow and set into state
      setTableData(Array.isArray(payload) ? payload.map(mapRow) : []);
      setShowContainers(true);
    } catch (err) {
      console.error("ff-product-wise call failed", err);
      setError("Failed to fetch data");
      setTableData([]);
      setShowContainers(true);
    } finally {
      setIsLoading(false);
    }
  };

  // Edit handlers
  const handleEditClick = (visitId, productId, currentQuantity) => {
    const rowKey = `${visitId}_${productId}`;
    setEditingRowKey(rowKey);
    setEditedQuantities({
      ...editedQuantities,
      [rowKey]: currentQuantity,
    });
  };

  const handleCancelEdit = () => {
    setEditingRowKey(null);
    setEditedQuantities({});
  };

  const handleQuantityChange = (visitId, productId, value) => {
    const rowKey = `${visitId}_${productId}`;
    setEditedQuantities({
      ...editedQuantities,
      [rowKey]: value,
    });
  };

  const handleUpdateQuantity = async (row) => {
    const visitId = row.visit_id;
    const productId = row.product_id;
    const rowKey = `${visitId}_${productId}`;
    const newQuantity = editedQuantities[rowKey];

    if (!newQuantity || newQuantity === "" || isNaN(newQuantity)) {
      toast.err("Please enter a valid quantity");
      return;
    }

    try {
      const payload = [
        {
          product_id: productId,
          quantity: Number(newQuantity),
        },
      ];

      await axios.put(
        `${BASE_URL}/api/visits/update-visit-product-quantity/${visitId}`,
        payload,
        { headers: getAuthHeaders() },
      );

      toast.success("Quantity updated successfully");

      // Update table data - match by both visit_id and product_id
      setTableData((prevData) =>
        prevData.map((item) =>
          item.visit_id === visitId && item.product_id === productId
            ? { ...item, quantity: Number(newQuantity) }
            : item,
        ),
      );

      setEditingRowKey(null);
      setEditedQuantities({});
    } catch (err) {
      console.error("Error updating quantity:", err);
      toast.err("Failed to update quantity");
    }
  };

  const buildHeaders = () => [
    "S.No",
    "Emp ID",
    "Employee",
    "State",
    "DOJ",
    "Manager",
    "Designation",
    "Division",
    "HQ",
    "Working Date",
    "Retailer",
    "Product Code",
    "Product",
    "Qty",
    "Rate",
    "Amount",
  ];

  const formatForCsv = (row) => {
    const r = mapRow(row);
    return [
      r.emp_code,
      r.user_id,
      r.employee,
      r.state,
      r.doj,
      r.manager,
      r.designation,
      r.division,
      r.headquarter,
      r.working_date,
      r.retailer_name,
      r.product_code,
      r.product_name,
      String(r.quantity ?? 0),
      (Number(r.rate) ?? 0).toFixed(2),
      (Number(r.amount) ?? 0).toFixed(2),
    ];
  };

  const escapeCsv = (val) => {
    if (val === null || val === undefined) return "";
    const s = String(val);
    if (/[,"\n]/.test(s)) {
      return `"${s.replace(/"/g, '""')}"`;
    }
    return s;
  };

  const downloadCSV = () => {
    if (!tableData || tableData.length === 0) return;
    const rows = [buildHeaders(), ...tableData.map(formatForCsv)];
    const csv = rows
      .map((r) => r.map((c) => escapeCsv(c)).join(","))
      .join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const dateSuffix = new Date().toISOString().slice(0, 10).replace(/-/g, "");
    const filename = `ff-product-wise-${dateSuffix}.csv`;
    if (navigator.msSaveBlob) {
      navigator.msSaveBlob(blob, filename);
      return;
    }
    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);
    link.href = url;
    link.setAttribute("download", filename);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const handlePrint = () => {
    if (!tableData || tableData.length === 0) return;
    const stateName = states.find(
      (s) => String(s.id) === String(selectedState),
    )?.name;
    const divisionName =
      selectedDivision !== "All"
        ? divisions.find((d) => String(d.id) === String(selectedDivision))?.name
        : "All";
    const employeeLabel = selectedEmployee === "All" ? "All" : selectedEmployee;
    const headerHtml = `<div style="font-family: Arial, sans-serif; margin-bottom: 8px;">
      <h3 style="margin:0">FF Product Wise Report</h3>
      <div style="font-size:13px; color:#444; margin-top:6px;">
        <strong>State:</strong> ${stateName || ""} &nbsp; | &nbsp;
        <strong>Division:</strong> ${divisionName} &nbsp; | &nbsp;
        <strong>Employee:</strong> ${employeeLabel} &nbsp; | &nbsp;
        <strong>From:</strong> ${startDate} &nbsp; <strong>To:</strong> ${endDate}
      </div>
    </div>`;
    const style = `
      <style>
        table { width:100%; border-collapse: collapse; font-family: Arial, sans-serif; font-size: 11px;}
        th, td { border: 1px solid #ddd; padding: 6px; text-align: left; vertical-align: top; }
        th { background: #f0f9ff; font-weight: 600; }
        .text-right { text-align: right; }
      </style>
    `;
    const rowsHtml = tableData
      .map((row, idx) => {
        const r = mapRow(row);
        return `<tr>
          <td>${idx + 1}</td>
          <td>${escapeHtml(r.user_id)}</td>
          <td>${escapeHtml(r.employee)}</td>
          <td>${escapeHtml(r.state)}</td>
          <td>${escapeHtml(r.doj)}</td>
          <td>${escapeHtml(r.manager)}</td>
          <td>${escapeHtml(r.designation)}</td>
          <td>${escapeHtml(r.division)}</td>
          <td>${escapeHtml(r.headquarter)}</td>
          <td>${escapeHtml(r.working_date)}</td>
          <td>${escapeHtml(r.retailer_name)}</td>
          <td>${escapeHtml(r.product_code)}</td>
          <td>${escapeHtml(r.product_name)}</td>
          <td class="text-right">${escapeHtml(r.quantity)}</td>
          <td class="text-right">${Number(r.rate).toFixed(2)}</td>
          <td class="text-right">${Number(r.amount).toFixed(2)}</td>
        </tr>`;
      })
      .join("");
    const tableHtml = `
      <table>
        <thead>
          <tr>
            ${buildHeaders()
              .map((h) => `<th>${h}</th>`)
              .join("")}
          </tr>
        </thead>
        <tbody>
          ${rowsHtml}
        </tbody>
      </table>
    `;
    const printWindow = window.open("", "_blank", "width=1000,height=800");
    if (!printWindow) {
      toast.err(
        "Unable to open print window. Please allow popups for this site.",
      );
      return;
    }
    printWindow.document.open();
    printWindow.document.write(`
      <html>
        <head>
          <title>FF Product Wise Report</title>
          ${style}
        </head>
        <body>
          ${headerHtml}
          ${tableHtml}
        </body>
      </html>
    `);
    printWindow.document.close();
    printWindow.onload = () => {
      printWindow.focus();
      printWindow.print();
    };
  };

  const escapeHtml = (unsafe) => {
    if (unsafe === null || unsafe === undefined) return "";
    return String(unsafe)
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;")
      .replace(/'/g, "&#039;");
  };

  return (
    <div className="p-2  border-2 border-gray-300 rounded-lg bg-white">
      {isLoading && <Loader onCancel={() => setIsLoading(false)} />}
      <div className=" border-2 border-gray-300 rounded-lgp-4 mb-2">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-2xl font-bold flex justify-center gap-2 mb-4">
            <span className="text-blue-700">FF RTR Wise</span>
            <span className="text-blue-500">Product</span>
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 text-sm">
            <div className="space-y-2">
              <div className="space-y-2">
                <div className="flex items-center">
                  <label className="w-24 font-medium text-gray-800">
                    State
                  </label>
                  <select
                    value={selectedState}
                    onChange={(e) => setSelectedState(e.target.value)}
                    className="flex-1 border border-gray-400 rounded-sm h-8 px-2"
                  >
                    {states.map((s) => (
                      <option key={s.id} value={s.id}>
                        {s.name}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
              <div className="flex items-center">
                <label className="w-24 font-medium text-gray-800">
                  Division
                </label>
                <select
                  value={selectedDivision}
                  onChange={(e) => setSelectedDivision(e.target.value)}
                  disabled={!selectedState}
                  className="flex-1 border border-gray-400 rounded-sm h-8 px-2 bg-white disabled:opacity-60 "
                >
                  {divisions.map((d) => (
                    <option key={d.id} value={d.id}>
                      {d.name}
                    </option>
                  ))}
                </select>
              </div>
              <div className="flex items-center">
                <label className="w-24 font-medium text-gray-800">
                  Start Date
                </label>
                <input
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  className="border border-gray-400 rounded-sm h-8 px-2 w-36 "
                />
              </div>
            </div>
            <div className="space-y-2">
              <div className="flex items-center">
                <label className="w-24 font-medium text-gray-800">
                  Sub State
                </label>
                <select
                  value={selectedSubState}
                  onChange={(e) => setSelectedSubState(e.target.value)}
                  disabled={!selectedState}
                  className="flex-1 border border-gray-400 rounded-sm h-8 px-2 bg-white disabled:opacity-60 "
                >
                  <option value="">Select Sub State</option>
                  {substates.map((s) => (
                    <option key={s.id} value={s.id}>
                      {s.name}
                    </option>
                  ))}
                </select>
              </div>
              <div className="flex items-center">
                <label className="w-24 font-medium text-gray-800">
                  Employee
                </label>
                <select
                  value={selectedEmployee}
                  onChange={(e) => setSelectedEmployee(e.target.value)}
                  disabled={!selectedState || isLoading}
                  className="flex-1 border border-gray-400 rounded-sm h-8 px-2 bg-white disabled:opacity-60 "
                >
                  {employees.map((emp) => (
                    <option key={emp.id} value={emp.id}>
                      {emp.id === "All"
                        ? "All"
                        : `${emp.first_name} ${emp.last_name} ${emp.employee} (${
                            emp.designation || ""
                          } ${emp.division} ${
                            emp.headquarter_name
                              ? `| ${emp.headquarter_name}`
                              : ""
                          })`}
                    </option>
                  ))}
                </select>
              </div>
              <div className="flex items-center gap-3">
                <label className="w-24 font-medium text-gray-800">
                  End Date
                </label>
                <input
                  type="date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  className="border border-gray-400 rounded-sm h-8 px-2 w-36 "
                />
              </div>
            </div>
          </div>
          <div className="flex justify-center mt-4">
            <CustomButton
              variant="go"
              onClick={handleGoClick}
              disabled={isLoading}
              className="break-normal"
            >
              {isLoading ? "Loading..." : "GO"}
            </CustomButton>
          </div>
          {error && (
            <div className="text-sm text-red-600 mt-2 text-center">{error}</div>
          )}
        </div>
      </div>

      {showContainers && (
        <div className="border rounded shadow  table-wrapper borde-2 border-gray-300 ">
          <table className="unlock-dcr-leave-table text-sm">
            <thead className="bg-blue-500 text-white">
              <tr>
                <th className="border px-1 py-1">S.No</th>
                <th className="border px-1 py-1">Emp ID</th>

                <th className="border px-1 py-1">State</th>
                <th className="border px-1 py-1">Sub State</th>
                <th className="border px-1 py-1">Employee Name</th>
                <th className="border px-1 py-1">Designation</th>
                <th className="border px-1 py-1">HQ</th>
                <th className="border px-1 py-1">Division</th>
                <th className="border px-1 py-1">DOJ</th>
                <th className="border px-1 py-1">Manager</th>

                <th className="border px-1 py-1">Working Date</th>
                <th className="border px-1 py-1">Retailer Name</th>
                <th className="border px-1 py-1">Product Code</th>
                <th className="border px-1 py-1">Product Name</th>
                <th className="border px-1 py-1 text-right">Qty</th>
                <th className="border px-1 py-1 text-right">Retailer Price</th>
                <th className="border px-1 py-1 text-right">Amount</th>
                <th className="border px-1 py-1">Action</th>
              </tr>
            </thead>
            <tbody>
              {tableData && tableData.length > 0 ? (
                tableData.map((raw, idx) => {
                  const r = mapRow(raw);
                  const rowKey = `${r.visit_id}_${r.product_id}`;
                  const isEditing = editingRowKey === rowKey;
                  return (
                    <tr
                      key={idx}
                      className={idx % 2 === 0 ? "bg-white" : "bg-gray-50"}
                    >
                      <td className="border px-1 py-1 text-center">
                        {idx + 1}
                      </td>
                      <td className="border px-1 py-1">{r.user_id}</td>
                      <td className="border px-1 py-1">{r.state}</td>
                      <td className="border px-1 py-1">{r.sub_state || "-"}</td>
                      <td className="border px-1 py-1 whitespace-normal break-words">
                        {r.employee}
                      </td>
                      <td className="border px-1 py-1">{r.designation}</td>
                      {/* use normalized sub_state which mapRow now provides */}
                      <td className="border px-1 py-1">{r.headquarter}</td>
                      <td className="border px-1 py-1">{r.division}</td>
                      <td className="border px-1 py-1">
                        {formatReadableDate(r.doj)}
                      </td>
                      <td className="border px-1 py-1 whitespace-normal break-words">
                        {r.manager}
                      </td>

                      <td className="border px-1 py-1">
                        {formatReadableDate(r.working_date)}
                      </td>
                      <td className="border px-1 py-1 max-w-[140px] whitespace-normal break-words">
                        {r.retailer_name}
                      </td>
                      <td className="border px-1 py-1">{r.product_id}</td>
                      <td
                        className="border px-1 py-1 max-w-[200px] truncate whitespace-normal break-words"
                        title={r.product_name}
                      >
                        {r.product_name}
                      </td>
                      <td className="border px-1 py-1 text-right">
                        {isEditing ? (
                          <input
                            type="number"
                            step="0.01"
                            value={editedQuantities[rowKey] ?? r.quantity}
                            onChange={(e) =>
                              handleQuantityChange(
                                r.visit_id,
                                r.product_id,
                                e.target.value,
                              )
                            }
                            className="w-20 border border-gray-400 rounded px-1 py-0.5 text-right"
                          />
                        ) : r.quantity === null || r.quantity === undefined ? (
                          "-"
                        ) : (
                          Number(r.quantity || 0).toFixed(2)
                        )}
                      </td>
                      <td className="border px-1 py-1 text-right">
                        {Math.floor(r.rate)}
                      </td>
                      <td className="border px-1 py-1 text-right">
                        {Math.floor(r.amount)}
                      </td>
                      <td className="border px-1 py-1 text-center">
                        {isEditing ? (
                          <div className="flex gap-1 justify-center">
                            <CustomButton
                              variant="save"
                              className="h-9 px-6 rounded"
                              onClick={() => handleUpdateQuantity(r)}
                            >
                              Update
                            </CustomButton>
                            <CustomButton
                              variant="cancel"
                              className="h-9 px-6 rounded"
                              onClick={handleCancelEdit}
                            >
                              Cancel
                            </CustomButton>
                          </div>
                        ) : (
                          <CustomButton
                            variant="edit"
                            className="h-9 px-6 rounded"
                            onClick={() =>
                              handleEditClick(
                                r.visit_id,
                                r.product_id,
                                r.quantity,
                              )
                            }
                          >
                            Edit
                          </CustomButton>
                        )}
                      </td>
                    </tr>
                  );
                })
              ) : (
                <tr>
                  <td className="border px-2 py-8 text-center" colSpan={18}>
                    No data found
                  </td>
                </tr>
              )}
            </tbody>
          </table>
          <div className="flex gap-3 justify-center mt-4">
            <CustomButton
              variant="print"
              label={"Print"}
              onClick={handlePrint}
              disabled={!tableData || tableData.length === 0}
            >
              🖨
            </CustomButton>
            <CustomButton
              variant="export"
              label={"Excel"}
              onClick={downloadCSV}
              disabled={!tableData || tableData.length === 0}
            >
              📥
            </CustomButton>
          </div>
        </div>
      )}
    </div>
  );
};

export default StateWiseProduct;
